import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { MouseWheelClassifier } from 'vscode/vscode/vs/base/browser/ui/scrollbar/scrollableElement';
import { Disposable, MutableDisposable, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { isMacintosh } from 'vscode/vscode/vs/base/common/platform';
import { registerTerminalContribution } from '../../../terminal/browser/terminalExtensions.js';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { registerTerminalAction } from '../../../terminal/browser/terminalActions.js';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { isNumber } from 'vscode/vscode/vs/base/common/types';
import { defaultTerminalFontSize } from '../../../terminal/common/terminalConfiguration.js';

var TerminalMouseWheelZoomContribution_1;
const _moduleId = "vs/workbench/contrib/terminalContrib/zoom/browser/terminal.zoom.contribution";
let TerminalMouseWheelZoomContribution = class TerminalMouseWheelZoomContribution extends Disposable {
    static { TerminalMouseWheelZoomContribution_1 = this; }
    static { this.ID = 'terminal.mouseWheelZoom'; }
    static get(instance) {
        return instance.getContribution(TerminalMouseWheelZoomContribution_1.ID);
    }
    constructor(instance, processManager, widgetManager, _configurationService) {
        super();
        this._configurationService = _configurationService;
        this._listener = this._register(( (new MutableDisposable())));
    }
    xtermOpen(xterm) {
        this._register(Event.runAndSubscribe(this._configurationService.onDidChangeConfiguration, e => {
            if (!e || e.affectsConfiguration("terminal.integrated.mouseWheelZoom" )) {
                if (!!this._configurationService.getValue("terminal.integrated.mouseWheelZoom" )) {
                    this._setupMouseWheelZoomListener(xterm.raw);
                }
                else {
                    this._listener.clear();
                }
            }
        }));
    }
    _getConfigFontSize() {
        return this._configurationService.getValue("terminal.integrated.fontSize" );
    }
    _setupMouseWheelZoomListener(raw) {
        const classifier = MouseWheelClassifier.INSTANCE;
        let prevMouseWheelTime = 0;
        let gestureStartFontSize = this._getConfigFontSize();
        let gestureHasZoomModifiers = false;
        let gestureAccumulatedDelta = 0;
        raw.attachCustomWheelEventHandler((e) => {
            const browserEvent = e;
            if (classifier.isPhysicalMouseWheel()) {
                if (this._hasMouseWheelZoomModifiers(browserEvent)) {
                    const delta = browserEvent.deltaY > 0 ? -1 : 1;
                    this._configurationService.updateValue("terminal.integrated.fontSize" , this._getConfigFontSize() + delta);
                    browserEvent.preventDefault();
                    browserEvent.stopPropagation();
                    return false;
                }
            }
            else {
                if (Date.now() - prevMouseWheelTime > 50) {
                    gestureStartFontSize = this._getConfigFontSize();
                    gestureHasZoomModifiers = this._hasMouseWheelZoomModifiers(browserEvent);
                    gestureAccumulatedDelta = 0;
                }
                prevMouseWheelTime = Date.now();
                gestureAccumulatedDelta += browserEvent.deltaY;
                if (gestureHasZoomModifiers) {
                    const deltaAbs = Math.ceil(Math.abs(gestureAccumulatedDelta / 5));
                    const deltaDirection = gestureAccumulatedDelta > 0 ? -1 : 1;
                    const delta = deltaAbs * deltaDirection;
                    this._configurationService.updateValue("terminal.integrated.fontSize" , gestureStartFontSize + delta);
                    gestureAccumulatedDelta += browserEvent.deltaY;
                    browserEvent.preventDefault();
                    browserEvent.stopPropagation();
                    return false;
                }
            }
            return true;
        });
        this._listener.value = toDisposable(() => raw.attachCustomWheelEventHandler(() => true));
    }
    _hasMouseWheelZoomModifiers(browserEvent) {
        return (isMacintosh
            ? ((browserEvent.metaKey || browserEvent.ctrlKey) && !browserEvent.shiftKey && !browserEvent.altKey)
            : (browserEvent.ctrlKey && !browserEvent.metaKey && !browserEvent.shiftKey && !browserEvent.altKey));
    }
};
TerminalMouseWheelZoomContribution = TerminalMouseWheelZoomContribution_1 = ( (__decorate([
    ( (__param(3, IConfigurationService)))
], TerminalMouseWheelZoomContribution)));
registerTerminalContribution(TerminalMouseWheelZoomContribution.ID, TerminalMouseWheelZoomContribution, true);
registerTerminalAction({
    id: "workbench.action.terminal.fontZoomIn" ,
    title: ( localize2WithPath(_moduleId, 0, 'Increase Font Size')),
    run: async (c, accessor) => {
        const configurationService = accessor.get(IConfigurationService);
        const value = configurationService.getValue("terminal.integrated.fontSize" );
        if (isNumber(value)) {
            await configurationService.updateValue("terminal.integrated.fontSize" , value + 1);
        }
    }
});
registerTerminalAction({
    id: "workbench.action.terminal.fontZoomOut" ,
    title: ( localize2WithPath(_moduleId, 1, 'Decrease Font Size')),
    run: async (c, accessor) => {
        const configurationService = accessor.get(IConfigurationService);
        const value = configurationService.getValue("terminal.integrated.fontSize" );
        if (isNumber(value)) {
            await configurationService.updateValue("terminal.integrated.fontSize" , value - 1);
        }
    }
});
registerTerminalAction({
    id: "workbench.action.terminal.fontZoomReset" ,
    title: ( localize2WithPath(_moduleId, 2, 'Reset Font Size')),
    run: async (c, accessor) => {
        const configurationService = accessor.get(IConfigurationService);
        await configurationService.updateValue("terminal.integrated.fontSize" , defaultTerminalFontSize);
    }
});
